import { useCallback, useEffect, useState } from 'react';

export interface Store {
  readonly read: <V = unknown>(key: string) => V | undefined;
  readonly write: <V = unknown>(key: string, value: V) => void;
  readonly watch: <V = unknown>(
    key: string,
    cb: (value: V | undefined) => void,
  ) => () => void;
  readonly values: KeyValueStore;
}

export function createStore(
  initialValue: KeyValueStore,
  onChange: (value: KeyValueStore) => void,
): Store {
  const store = initialValue;

  let changeListeners: readonly ((store: KeyValueStore) => void)[] = [];

  return {
    read<V = unknown>(key: string): V | undefined {
      return store[key] as V | undefined;
    },

    write<V = unknown>(key: string, value: V) {
      store[key] = value;
      onChange(store);
      for (const listener of changeListeners) {
        listener(store);
      }
    },

    watch<V = unknown>(
      key: string,
      cb: (value: V | undefined) => void,
    ): () => void {
      let previous = store[key] as V | undefined;
      const listener = (store: KeyValueStore) =>
        void (
          previous !== store[key] &&
          cb((previous = store[key] as V | undefined))
        );

      changeListeners = [...changeListeners, listener];

      return () =>
        void (changeListeners = changeListeners.filter(
          (changeListener) => changeListener !== listener,
        ));
    },

    get values() {
      return store;
    },
  };
}

export type KeyValueStore = Record<string, unknown>;

export function useStore<V>(store: Store, key: string) {
  const [value, setValue] = useState(() => store.read<V>(key));

  useEffect(() => {
    setValue(store.read(key));

    return store.watch<V>(key, setValue);
  }, [store]);

  const updateValue = useCallback(
    (sessionValue: V | undefined) => {
      store.write(key, sessionValue);
    },
    [store, key],
  );

  return [value, updateValue] as const;
}
